import { useContainerBlockContext, useCustomViewBlockContext } from '@lighthouse/block'
import type { ViewBlockAbstract } from '@lighthouse/core'
import { EVENT_TYPE } from '@lighthouse/core'
import type { FlowLayoutNode } from '@lighthouse/shared'
import { getWithScopeIdAndRecordIdAndColumnId, useActionRunningLoadings, useViewControlListenerIdList, ViewControlListenerIdsProvider } from '@lighthouse/shared'
import React, { useCallback, useMemo, useRef } from 'react'

import { useActionTrigger } from '@/hooks/useActionTrigger'

import AdvancedTableController from './AdvancedTableController'
import CalendarController from './CalendarController'
import CustomController from './CustomController'
import GalleryController from './GalleryController'
import KanbanController from './KanbanController'
import ListController from './ListController'
import TableController from './TableController'

interface ViewBlockControllerProps extends React.ComponentPropsWithoutRef<'div'> {
    blockData: ViewBlockAbstract
    node: FlowLayoutNode
}

const ViewBlockController: React.FC<ViewBlockControllerProps> = props => {
    const { blockData } = props
    const { id, config } = blockData
    const { viewType, eventList } = config
    const firstRef = useRef(true)
    const customViewContext = useCustomViewBlockContext()
    const { record } = customViewContext
    const { scope } = useContainerBlockContext()
    const uniqueId = useMemo(() => getWithScopeIdAndRecordIdAndColumnId(id, scope, record?.id), [id, scope, record?.id])
    const listenerIds = useViewControlListenerIdList(id, scope, customViewContext)
    const { handleActionListTrigger } = useActionTrigger(id)
    const { handleActionTriggerWithLoading } = useActionRunningLoadings()

    // 防止重复调用 loadAction
    const hasCalledLoadAction = useRef(false)

    // 子组件数据加载完成且渲染完成后的回调
    const handleChildReady = useCallback(() => {
        const loadEvent = eventList?.find(ev => ev.type === EVENT_TYPE.LOAD_DATA)
        if (!loadEvent || hasCalledLoadAction.current) {
            return
        }
        hasCalledLoadAction.current = true

        handleActionTriggerWithLoading({
            id: uniqueId,
            actorEvent: loadEvent,
            trigger: (id, actorEvent) => handleActionListTrigger(id, actorEvent, { viewRecord: record })
        })
    }, [eventList, handleActionListTrigger, handleActionTriggerWithLoading, record, uniqueId])

    const child = useMemo(() => {
        switch (viewType) {
            case 'table': {
                return <TableController firstRef={firstRef} onReady={handleChildReady} {...props} />
            }
            case 'list': {
                return <ListController firstRef={firstRef} onReady={handleChildReady} {...props} />
            }
            case 'advancedTable': {
                return <AdvancedTableController firstRef={firstRef} onReady={handleChildReady} {...props} />
            }

            case 'gallery': {
                return <GalleryController firstRef={firstRef} onReady={handleChildReady} {...props} />
            }

            case 'kanban': {
                return <KanbanController firstRef={firstRef} onReady={handleChildReady} {...props} />
            }

            case 'calendar': {
                return <CalendarController firstRef={firstRef} onReady={handleChildReady} {...props} />
            }

            case 'custom': {
                return <CustomController firstRef={firstRef} onReady={handleChildReady} {...props} />
            }

            default: {
                return null
            }
        }
    }, [handleChildReady, props, viewType])

    return <ViewControlListenerIdsProvider value={listenerIds}>{child}</ViewControlListenerIdsProvider>
}

export default ViewBlockController
